import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

/**
 * Source that triggered opening the command bar.
 * Used for analytics tracking.
 */
export type OpenSource = 'keyboard_shortcut' | 'button_click'

/**
 * Context value for controlling the command bar state.
 */
interface CommandBarContextValue {
  /** Whether the command bar is open */
  open: boolean
  /** Source that triggered opening the command bar */
  openSource: OpenSource | null
  /** Open the command bar */
  openCommandBar: (source?: OpenSource, returnFocusTo?: HTMLElement) => void
  /** Close the command bar */
  closeCommandBar: () => void
  /** Toggle the command bar */
  toggleCommandBar: (source?: OpenSource) => void
  /** Set the open state directly */
  setOpen: (open: boolean) => void
  restoreFocusOnClose: (event: Event) => void
}

const CommandBarContext = createContext<CommandBarContextValue | null>(null)

/**
 * Props for the CommandBarProvider component.
 */
interface CommandBarProviderProps {
  /** React children to render within the provider */
  children: ReactNode
}

function captureActiveElement(): HTMLElement | null {
  const active = document.activeElement
  if (active instanceof HTMLElement && active !== document.body) {
    return active
  }
  return null
}

export function CommandBarProvider({
  children,
}: CommandBarProviderProps): JSX.Element {
  const [open, setOpenState] = useState(false)
  const [openSource, setOpenSource] = useState<OpenSource | null>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)

  const captureReturnFocus = useCallback((returnFocusTo?: HTMLElement) => {
    if (returnFocusTo?.isConnected) {
      returnFocusRef.current = returnFocusTo
      return
    }
    returnFocusRef.current = captureActiveElement()
  }, [])

  const openCommandBar = useCallback(
    (source: OpenSource = 'button_click', returnFocusTo?: HTMLElement) => {
      captureReturnFocus(returnFocusTo)
      setOpenSource(source)
      setOpenState(true)
    },
    [captureReturnFocus],
  )
  const closeCommandBar = useCallback(() => {
    setOpenState(false)
    setOpenSource(null)
  }, [])
  const toggleCommandBar = useCallback(
    (source: OpenSource = 'keyboard_shortcut') => {
      setOpenState((prev) => {
        if (!prev) {
          captureReturnFocus()
          setOpenSource(source)
        } else {
          setOpenSource(null)
        }
        return !prev
      })
    },
    [captureReturnFocus],
  )
  const setOpen = useCallback(
    (nextOpen: boolean) => {
      setOpenState((prev) => {
        if (nextOpen && !prev) {
          captureReturnFocus()
        }
        return nextOpen
      })
      if (!nextOpen) {
        setOpenSource(null)
      }
    },
    [captureReturnFocus],
  )
  const restoreFocusOnClose = useCallback((event: Event) => {
    const target = returnFocusRef.current
    returnFocusRef.current = null
    if (!target?.isConnected) {
      return
    }
    event.preventDefault()
    target.focus()
  }, [])

  const value = useMemo(
    () => ({
      open,
      openSource,
      openCommandBar,
      closeCommandBar,
      toggleCommandBar,
      setOpen,
      restoreFocusOnClose,
    }),
    [
      open,
      openSource,
      openCommandBar,
      closeCommandBar,
      toggleCommandBar,
      setOpen,
      restoreFocusOnClose,
    ],
  )

  return (
    <CommandBarContext.Provider value={value}>
      {children}
    </CommandBarContext.Provider>
  )
}

export function useCommandBarContext(): CommandBarContextValue {
  const context = useContext(CommandBarContext)
  if (!context) {
    throw new Error(
      'useCommandBarContext must be used within a CommandBarProvider',
    )
  }
  return context
}
